import { useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { IconCircle } from "@tabler/icons-react";
import { useAppDispatch } from "@/redux/hook";
import { CreateReferrerNew } from "@/redux/features/actions/referrers";
import { Input } from "@/components/form";
import Button from "@/components/button";

const ReferrerUnprotected = () => {
  const [formKey, setFormKey] = useState(0);
  const initialValues = useMemo(
    () => ({
      name: "",
      phone_number: "",
      referrer_type: "Promo",
      discount: 0,
      province: "",
      district: "",
      submit: null,
    }),
    []
  );

  const dispatch = useAppDispatch();

  const submit = useCallback(
    async (
      values: typeof initialValues,
      { setErrors, setStatus, setSubmitting }: FormikHelpers<typeof initialValues>
    ) => {
      try {
        const { submit, ...data } = values;
        await dispatch(CreateReferrerNew(data)).unwrap();

        setStatus({ success: true });
        setFormKey(prev => prev + 1); // This will completely reset the form by remounting it
      } catch (err: any) {
        setStatus({ success: false });

        let errorMessage = "Something went wrong, please try again.";

        if (err?.response?.status === 409) {
          errorMessage = err?.response?.data?.message;
        } else if (err?.response?.data?.message) {
          errorMessage = Array.isArray(err.response.data.message)
            ? err.response.data.message.join(" ")
            : err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }

        setErrors({ submit: errorMessage });
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch],
  );

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg mt-10 shadow-md">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Promoters Registration
        </h1>
        <p className="text-gray-600">
          Fill in the form below to register a new promoter
        </p>
      </div>

      <Formik
        key={formKey}
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          name: Yup.string().required("Name is required"),
          phone_number: Yup.string().required("Phone number is required"),
          discount: Yup.number().required("Discount is required"),
          province: Yup.string().required("Province is required"),
          district: Yup.string().required("District is required"),
        })}
        onSubmit={submit}
        enableReinitialize
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit} className="space-y-6">
            {errors.submit && (
              <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded">
                <p className="text-red-700">{errors.submit}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="text"
                name="name"
                label="Full Name"
                placeholder="Enter full name"
                error={errors.name}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                isTouched={Boolean(touched.name)}
              />
              
              <Input
                type="text"
                name="phone_number"
                label="Phone Number"
                placeholder="Enter phone number"
                error={errors.phone_number}
                value={values.phone_number}
                onChange={handleChange}
                onBlur={handleBlur}
                isTouched={Boolean(touched.phone_number)}
              />
              
            
              
              <Input
                type="number"
                name="discount"
                label="Discount (%)"
                placeholder="Enter discount percentage"
                error={errors.discount}
             
                onChange={handleChange}
                onBlur={handleBlur}
                isTouched={Boolean(touched.discount)}
              />
              
              <Input
                type="text"
                name="province"
                label="Province"
                placeholder="Enter province"
                error={errors.province}
                value={values.province}
                onChange={handleChange}
                onBlur={handleBlur}
                isTouched={Boolean(touched.province)}
              />
              
              <Input
                type="text"
                name="district"
                label="District"
                placeholder="Enter district"
                error={errors.district}
                value={values.district}
                onChange={handleChange}
                onBlur={handleBlur}
                isTouched={Boolean(touched.district)}
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2"
                icon={isSubmitting && <IconCircle className="animate-spin" color="inherit" size={18} />}
              >
                Add Promoter
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default ReferrerUnprotected;